import { useMemo, useRef } from "react";
import { Box } from "@mantine/core";
import { FloorplanAreaLabels, FloorplanDoorMarkers, FloorplanRoomOverlay, FurnitureOverlay } from "@nodus/floorplan";
import type { NavigateInstruction } from "@/lib/api/client";
import { cutoutRect, itemsInView, stepPoints, viewForAspects, type Point, type Rect } from "./geometry";
import type { DirectionsPlan } from "./plan";
import { useDirectionsPlan } from "./useDirectionsPlan";
import { useNearViewport } from "./useNearViewport";

/** A cut-out is 280 px tall and as wide as the step list: from a small phone (~1:1) to a tablet below the desktop
 *  layout (~2.7:1). Rooms and doors anywhere such a box can show are drawn. */
const MIN_ASPECT = 1;
const MAX_ASPECT = 2.7;
const ALL_KINDS = { furniture: true, equipment: true, desk: true };
/** Draw a cut-out once it comes within this distance of the screen, so it is ready before it scrolls in. */
const PRELOAD_MARGIN = "600px 0px";
const BOX_STYLE = { borderRadius: 6, overflow: "hidden", border: "1px solid var(--mantine-color-default-border)", pointerEvents: "none" } as const;
const PLAN_STYLE = { display: "block", position: "absolute", inset: 0 } as const;
/** `will-change` gives the animated line a compositor layer of its own. */
const ROUTE_STYLE = { display: "block", position: "absolute", inset: 0, willChange: "transform" } as const;

/**
 * One direction's map: the part of the plan the step is about, with the floor's whole route drawn faintly and the
 * step's stretch as a marching red dashed line (or a dot where it points at one spot).
 *
 * A static picture, not a floor plan viewer: the drawing is the plan's image (decoded once per plan, see plan.ts)
 * framed by an SVG viewBox, with the viewer's own room, furniture, door and label layers on top for just the rooms
 * in view. It takes no input (the list scrolls over it), so it needs no zoom state, timers or measuring. The box
 * is laid out at once (the list keeps its height), and the picture is drawn when the box nears the screen.
 */
export function InstructionMapCutout({ instruction, fullPath }: { instruction: NavigateInstruction; fullPath?: Point[] }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const near = useNearViewport(boxRef, PRELOAD_MARGIN);
  const plan = useDirectionsPlan(instruction.floorplan_id);
  const view = useMemo(() => {
    const pts = stepPoints(instruction);
    return pts ? cutoutRect(pts, fullPath) : null;
  }, [instruction, fullPath]);

  // A plan that has loaded without a drawing, or a step with nothing to show: no map.
  if (!view || (plan && !plan.svg)) return null;
  return (
    <Box ref={boxRef} h={280} pos="relative" style={BOX_STYLE} mt={4}>
      {near && plan && <CutoutPicture plan={plan} view={view} instruction={instruction} fullPath={fullPath} />}
    </Box>
  );
}

function CutoutPicture({ plan, view, instruction, fullPath }: { plan: DirectionsPlan; view: Rect; instruction: NavigateInstruction; fullPath?: Point[] }) {
  const layers = useMemo(() => {
    const around = viewForAspects(view, MIN_ASPECT, MAX_ASPECT);
    return {
      rooms: itemsInView(plan.foldedRooms, (r) => r.polygon?.flat(), around),
      doors: itemsInView(plan.doors, (d) => (d.geometry?.type === "Point" ? [d.geometry.coordinates] : null), around),
    };
  }, [plan, view]);
  const vb = plan.viewBox;
  const viewBox = `${view.x} ${view.y} ${view.w} ${view.h}`;
  // Two pictures with the same framing: the plan (still) and, over it on its own layer, the step's marching line.
  // The line animates every frame; sharing a layer with the plan, every frame would repaint the plan with it.
  return (
    <>
      <svg viewBox={viewBox} preserveAspectRatio="xMidYMid meet" width="100%" height="100%" style={PLAN_STYLE} aria-hidden>
        {plan.imageUrl && vb && (
          <image href={plan.imageUrl} x={vb.x} y={vb.y} width={vb.w} height={vb.h} preserveAspectRatio="none" />
        )}
        <FloorplanRoomOverlay rooms={layers.rooms} mode="polygonsOnly" />
        <FurnitureOverlay rooms={layers.rooms} scale={plan.scale} kindsVisible={ALL_KINDS} floorFurniture={plan.furniture} />
        <FloorplanDoorMarkers doors={layers.doors} />
        <FloorplanAreaLabels rooms={layers.rooms} />
        <FloorplanRoomOverlay rooms={layers.rooms} mode="labelsOnly" />
        {fullPath && fullPath.length >= 2 && (
          <polyline points={fullPath.map((p) => p.join(",")).join(" ")} fill="none" stroke="#3b82f6" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" opacity={0.25} />
        )}
      </svg>
      <svg viewBox={viewBox} preserveAspectRatio="xMidYMid meet" width="100%" height="100%" style={ROUTE_STYLE} aria-hidden>
        {instruction.path_portion && instruction.path_portion.length >= 2 && (
          <polyline points={instruction.path_portion.map((p) => p.join(",")).join(" ")} fill="none" stroke="#ef4444" strokeWidth={4} strokeLinecap="round" strokeLinejoin="round" strokeDasharray="8,5">
            <animate attributeName="stroke-dashoffset" from="26" to="0" dur="1s" repeatCount="indefinite" />
          </polyline>
        )}
        {instruction.focus && <circle cx={instruction.focus[0]} cy={instruction.focus[1]} r={6} fill="#ef4444" stroke="#fff" strokeWidth={2} />}
      </svg>
    </>
  );
}
